import { Translation } from '@ps/web-shared-components';
import React from 'react';
import { useSelector } from 'react-redux';

import Redirect from '@swf/components/Redirect/Redirect';
import { Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import { Header } from '@swf/core/enums';
import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import { CompetitionCard, CompetitionTabs, Message, TryAgainMessage } from '@swf/sports/components';
import { Breadcrumb } from '@swf/sports/components/Breadcrumb/Breadcrumb';
import GroupedMarketTemplate from '@swf/sports/components/GroupedMarketTemplate/GroupedMarketTemplate';
import useMarketUpdates from '@swf/sports/hooks/useMarketUpdates';
import { EventTypeId } from '@swf/sports/types/eventType';
import { isSingleMarketTemplate } from '@swf/sports/utils/isSingleMarketTemplate';

import { TXTWCL_There_are_no_markets_available_ELL } from './SportsGolfMatchesWidget.messages.json';
import { sportsGolfMatchesWidgetModel, useSportsGolfMatchesWidget } from './model';

const INITIALLY_OPEN_GROUPS = 5;

const WidgetContent = () => {
  const { widgetSelect } = useSportsGolfMatchesWidget();

  const markets = useSelector(widgetSelect.markets);
  const marketIds = useSelector(widgetSelect.marketIds);
  const groupedTemplates = useSelector(widgetSelect.templateGroups);

  const [firstMarketId = ''] = marketIds;
  const competitionId = markets[firstMarketId]?.competitionId ?? 0;

  const competitionName = useSelector(
    (rootState: RootState) => widgetSelect.competition(rootState, competitionId).competitionName
  );

  return (
    <>
      <Breadcrumb />
      <Vary by={[Header.TimeZone]} />
      {competitionName && <CompetitionCard competitionName={competitionName} />}
      <CompetitionTabs />
      {groupedTemplates.map((groupedTemplate, index) => {
        const [[template] = []] = groupedTemplate;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!template) return null;

        return (
          <GroupedMarketTemplate
            key={`group-${
              isSingleMarketTemplate(template) ? template.marketId : template.marketIds.join()
            }`}
            groupedMarkets={groupedTemplate}
            openInitially={index < INITIALLY_OPEN_GROUPS}
          />
        );
      })}
    </>
  );
};

const SportsGolfMatchesWidget: WidgetFC = () => {
  const { widgetDispatch, widgetSelect } = useSportsGolfMatchesWidget();

  const markets = useSelector(widgetSelect.markets);
  const marketIds = useSelector(widgetSelect.marketIds);
  const groupedTemplates = useSelector(widgetSelect.templateGroups);
  const error = useSelector(widgetSelect.error);
  const isLoading = useSelector(widgetSelect.isLoading);
  const urlParams = useSelector(select.sports.getUrlParams);

  useMarketUpdates(marketIds, widgetDispatch.golfMatchesMarketsUpdated);

  if (isLoading) {
    return null;
  }

  if (error) {
    if (error === 404) {
      return (
        <Redirect
          alias="sportsCompetitionOutrights"
          values={{ ...urlParams, eventTypeId: EventTypeId.GOLF }}
        />
      );
    }
    return <TryAgainMessage />;
  }

  if (groupedTemplates.length === 0 || Object.keys(markets).length === 0) {
    return <Message message={<Translation {...TXTWCL_There_are_no_markets_available_ELL} />} />;
  }

  return <WidgetContent />;
};

SportsGolfMatchesWidget.model = sportsGolfMatchesWidgetModel;

export default withWidgetTestId(SportsGolfMatchesWidget, 'sports-golf-matches-widget');
